import React, { useEffect, useMemo, useState } from 'react';
import { colors } from '../theme';
import LoadingSpinner from './LoadingSpinner';

const STAGE_OPTIONS = [
  'Pending',
  'Initiated',
  'Assigned',
  'Received',
  'Repairing',
  'Ready',
  'Shipped',
  'Assessment',
  'Waiting for Customer Approval',
  'Customer Approved Repair',
  'Repair Cancelled by Customer',
  'Repair Started',
  'Repair Finished',
  'Repair Finished (Back in Stock/Refurbished)',
  'Further Analysis Required',
  'Closed'
];

const canonicalStage = value => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  if (normalized.toLowerCase() === 'submitted') {
    return 'Pending';
  }
  if (normalized.toLowerCase() === 'pending') {
    return 'Pending';
  }
  return normalized;
};

function formatFileSize(size) {
  if (typeof size !== 'number' || Number.isNaN(size)) return null;
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (size >= 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }
  return `${size} B`;
}

function formatTimestamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

export default function GpvRmaFollowupPanel({
  deviceId,
  followup,
  serialNumber,
  latestStage,
  apiBase,
  getToken,
  onFollowupUpdated
}) {
  const [rmaIdInput, setRmaIdInput] = useState(followup?.rmaId ?? '');
  const [stage, setStage] = useState(() => canonicalStage(followup?.stage ?? (latestStage || '')));
  const [investigationNotes, setInvestigationNotes] = useState(followup?.investigation?.notes ?? '');
  const [eightDNotes, setEightDNotes] = useState(followup?.eightD?.notes ?? '');
  const [newInvestigationFiles, setNewInvestigationFiles] = useState([]);
  const [newEightDFiles, setNewEightDFiles] = useState([]);
  const [pendingInvestigationRemovals, setPendingInvestigationRemovals] = useState([]);
  const [pendingEightDRemovals, setPendingEightDRemovals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setRmaIdInput(followup?.rmaId ?? '');
    setStage(canonicalStage(followup?.stage ?? (latestStage || '')));
    setInvestigationNotes(followup?.investigation?.notes ?? '');
    setEightDNotes(followup?.eightD?.notes ?? '');
    setPendingInvestigationRemovals([]);
    setPendingEightDRemovals([]);
    setNewInvestigationFiles([]);
    setNewEightDFiles([]);
    setError('');
    setSuccess('');
  }, [followup, latestStage]);

  const investigationFiles = followup?.investigation?.files ?? [];
  const eightDFiles = followup?.eightD?.files ?? [];
  const audit = followup?.audit ?? null;

  const resolvedBase = useMemo(() => {
    if (!apiBase) return '';
    return apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
  }, [apiBase]);

  const isSaveDisabled = saving || !deviceId;

  const toggleInvestigationRemoval = (fileId) => {
    if (!fileId) return;
    setPendingInvestigationRemovals(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const toggleEightDRemoval = (fileId) => {
    if (!fileId) return;
    setPendingEightDRemovals(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  const handleInvestigationFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setNewInvestigationFiles(prev => [...prev, ...files]);
    event.target.value = '';
  };

  const handleEightDFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setNewEightDFiles(prev => [...prev, ...files]);
    event.target.value = '';
  };

  const removeNewInvestigationFile = (index) => {
    setNewInvestigationFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const removeNewEightDFile = (index) => {
    setNewEightDFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleSave = async () => {
    if (!deviceId) {
      setError('Device identifier missing; unable to save follow-up.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const endpoint = `${resolvedBase}/device/${encodeURIComponent(deviceId)}/gpv-followup`;
      const formData = new FormData();
      formData.append('rmaId', rmaIdInput.trim());
      formData.append('stage', stage);
      formData.append('investigationNotes', investigationNotes);
      formData.append('eightDNotes', eightDNotes);
      if (serialNumber) {
        formData.append('serialNumber', serialNumber);
      }
      pendingInvestigationRemovals.forEach(id => formData.append('removeInvestigationFileIds[]', id));
      pendingEightDRemovals.forEach(id => formData.append('removeEightDFileIds[]', id));
      newInvestigationFiles.forEach(file => formData.append('investigationFiles', file));
      newEightDFiles.forEach(file => formData.append('eightDFiles', file));

      const token = getToken ? await getToken() : null;
      const requestOptions = {
        method: 'PUT',
        body: formData
      };
      if (token) {
        requestOptions.headers = { Authorization: `Bearer ${token}` };
      }

      const response = await fetch(endpoint, requestOptions);

      if (!response.ok) {
        let message = 'Failed to save follow-up.';
        try {
          const payload = await response.json();
          if (payload?.message) message = payload.message;
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const payload = await response.json();
      if (onFollowupUpdated) {
        const nextFollowup = payload?.gpvRmaFollowup ?? payload;
        onFollowupUpdated(nextFollowup);
      }
      setSuccess('Follow-up details saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save follow-up.');
    } finally {
      setSaving(false);
    }
  };

  const renderExistingFile = (file, type) => {
    if (!file) return null;
    const fileId = file.id || file._id || file.filename || file.storageKey;
    const pendingRemoval = type === 'investigation'
      ? pendingInvestigationRemovals.includes(fileId)
      : pendingEightDRemovals.includes(fileId);
    const toggle = type === 'investigation' ? toggleInvestigationRemoval : toggleEightDRemoval;
    const label = file.originalName || file.filename || 'Attachment';
    const size = formatFileSize(file.size);
    const timestamp = formatTimestamp(file.uploadedAt);
    return (
      <li key={`${type}-${fileId}`} style={{ marginBottom: '0.35rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <a
          href={file.url || '#'}
          target={file.url ? '_blank' : undefined}
          rel={file.url ? 'noreferrer noopener' : undefined}
          style={{
            color: colors.accent,
            textDecoration: pendingRemoval ? 'line-through' : 'underline',
            opacity: pendingRemoval ? 0.6 : 1
          }}
        >
          {label}{size ? ` (${size})` : ''}
        </a>
        {timestamp && <span style={{ fontSize: '0.8rem', color: colors.muted }}>{timestamp}</span>}
        {file.uploadedBy && <span style={{ fontSize: '0.8rem', color: colors.muted }}>by {file.uploadedBy}</span>}
        <button
          type="button"
          onClick={() => toggle(fileId)}
          style={{
            background: 'none',
            border: 'none',
            color: pendingRemoval ? colors.accent : colors.danger,
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.8rem'
          }}
        >
          {pendingRemoval ? 'Undo remove' : 'Remove'}
        </button>
      </li>
    );
  };

  return (
    <section
      style={{
        backgroundColor: 'rgba(70, 187, 137, 0.08)',
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        padding: '1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <h3 style={{ margin: 0, color: colors.text, fontWeight: 700 }}>GPV Follow-up</h3>
          <p style={{ margin: '0.25rem 0 0', color: colors.muted, fontSize: '0.9rem' }}>Attach reports and track the current repair status for coordination with North Invent.</p>
        </div>
        <div style={{ alignSelf: 'center', color: colors.muted, fontSize: '0.85rem' }}>
          Serial: <strong>{serialNumber || 'Unknown'}</strong>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontWeight: 600, color: colors.text }}>RMA ID</span>
          <input
            value={rmaIdInput}
            onChange={event => setRmaIdInput(event.target.value)}
            placeholder='Enter reference RMA ID'
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.55rem 0.75rem',
              fontSize: '0.95rem'
            }}
          />
        </div>

        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontWeight: 600, color: colors.text }}>RMA Stage</span>
          <select
            value={stage}
            onChange={event => setStage(event.target.value)}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.55rem 0.75rem',
              fontSize: '0.95rem',
              backgroundColor: colors.background
            }}
          >
            <option value=''>Select stage</option>
            {STAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontWeight: 600, color: colors.text }}>Investigation Report</span>
          <textarea
            value={investigationNotes}
            onChange={event => setInvestigationNotes(event.target.value)}
            rows={3}
            placeholder='Add summary of investigation findings'
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.55rem 0.75rem',
              fontSize: '0.95rem',
              resize: 'vertical'
            }}
          />
          <div style={{ display: 'grid', gap: '0.35rem' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                color: colors.accent,
                fontWeight: 600
              }}
            >
              <span>Upload files</span>
              <input type='file' multiple onChange={handleInvestigationFileChange} style={{ display: 'none' }} />
            </label>
            {(investigationFiles.length > 0 || newInvestigationFiles.length > 0) && (
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {investigationFiles.map(file => renderExistingFile(file, 'investigation'))}
                {newInvestigationFiles.map((file, index) => (
                  <li key={`new-investigation-${index}`} style={{ marginBottom: '0.35rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: colors.text }}>{file.name}{formatFileSize(file.size) ? ` (${formatFileSize(file.size)})` : ''}</span>
                    <button
                      type='button'
                      onClick={() => removeNewInvestigationFile(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.danger,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '0.8rem'
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <span style={{ fontWeight: 600, color: colors.text }}>8D Report</span>
          <textarea
            value={eightDNotes}
            onChange={event => setEightDNotes(event.target.value)}
            rows={3}
            placeholder='Document status of 8D actions or provide context for attachments'
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              padding: '0.55rem 0.75rem',
              fontSize: '0.95rem',
              resize: 'vertical'
            }}
          />
          <div style={{ display: 'grid', gap: '0.35rem' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer',
                color: colors.accent,
                fontWeight: 600
              }}
            >
              <span>Upload files</span>
              <input type='file' multiple onChange={handleEightDFileChange} style={{ display: 'none' }} />
            </label>
            {(eightDFiles.length > 0 || newEightDFiles.length > 0) && (
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {eightDFiles.map(file => renderExistingFile(file, 'eightD'))}
                {newEightDFiles.map((file, index) => (
                  <li key={`new-eightd-${index}`} style={{ marginBottom: '0.35rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: colors.text }}>{file.name}{formatFileSize(file.size) ? ` (${formatFileSize(file.size)})` : ''}</span>
                    <button
                      type='button'
                      onClick={() => removeNewEightDFile(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: colors.danger,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '0.8rem'
                      }}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {(error || success) && (
        <div style={{ fontSize: '0.9rem', color: error ? colors.danger : colors.accent }}>
          {error || success}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
        {audit && (
          <div style={{ color: colors.muted, fontSize: '0.8rem' }}>
            Last updated {formatTimestamp(audit.updatedAt || audit.createdAt) || 'recently'}
            {audit.updatedBy || audit.createdBy ? ` by ${audit.updatedBy || audit.createdBy}` : ''}
          </div>
        )}
        <button
          type='button'
          onClick={handleSave}
          disabled={isSaveDisabled}
          style={{
            backgroundColor: isSaveDisabled ? colors.muted : colors.accent,
            color: colors.background,
            border: 'none',
            borderRadius: '999px',
            padding: '0.6rem 1.8rem',
            fontWeight: 600,
            cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: isSaveDisabled ? 0.75 : 1
          }}
        >
          {saving && <LoadingSpinner size={16} />}
          <span>{saving ? 'Saving...' : 'Save'}</span>
        </button>
      </div>
    </section>
  );
}
